'use client';
import { SplitScreenComponent } from '@payments-checkout/shared/components';
import { analiticaStatusPageEvents } from '@payments-checkout/shared/plugins/analitica/statusPage.events';
import { Button } from '@stone-payments/jade';
import { useIntl } from 'react-intl';
import { Delivery } from '../../types/sessionStatus.types';
import { BottomDetails } from '../bottomDetails/bottomDetails';
import { DeliveryStatus } from '../deliveryStatus/deliveryStatus';
import { Feedback } from '../feedback/feedback';
import type { OrderStatusStep } from '../orderStatusTracker/orderStatusTracker';
import { PageRefreshTimer } from '../pageRefreshTimer/pageRefreshTimer';
import { Summary } from '../saleSummary';

type FeedbackProps = {
  title?: string;
  subtitle?: string;
  amount?: string;
  icon?: string;
  variant?: string;
  offeredBy?: string;
  orderId?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  statusTracker?: OrderStatusStep[];
  alert?: {
    description: string;
  };
};

type TrackingStatusProps = {
  leftContent?: React.JSX.Element;
  feedback?: FeedbackProps;
  showSplitScreen?: boolean;
  showRefreshTimer?: boolean;
  backToStoreUrl?: string;
  paymentInfo?: React.JSX.Element;
  details?: React.JSX.Element;
  hasDelivery?: boolean;
  deliveryData?: Delivery;
  sessionId?: string;
  customer?: any;
};

export function TrackingStatus({
  leftContent,
  feedback,
  showSplitScreen = true,
  showRefreshTimer,
  backToStoreUrl,
  paymentInfo,
  details,
  hasDelivery = false,
  deliveryData,
  sessionId,
  customer,
}: TrackingStatusProps = {}) {
  const { formatMessage } = useIntl();
  const displayRefreshTimer = showRefreshTimer ?? showSplitScreen;
  const content = (
    <div className="w-full flex items-center justify-center pt-0 md:pt-jade-600 pb-jade-0">
      <div className="flex w-full max-w-[416px] flex-col gap-jade-200 px-jade-200 md:max-w-[464px]">
        {displayRefreshTimer && sessionId && (
          <PageRefreshTimer minutesToRefresh={2} sessionId={sessionId} />
        )}
        <Feedback
          title={feedback?.title || ''}
          subtitle={feedback?.subtitle || ''}
          amount={feedback?.amount}
          icon={feedback?.icon as any}
          variant={feedback?.variant as any}
          offeredBy={feedback?.offeredBy}
          orderId={feedback?.orderId}
          actionLabel={feedback?.actionLabel}
          onAction={feedback?.onAction}
          actionDisabled={feedback?.actionDisabled}
          statusTracker={feedback?.statusTracker}
          alert={feedback?.alert}
        />
        {hasDelivery && <DeliveryStatus deliveryData={deliveryData} />}
        {details ?? (
          <Summary
            compactLayout
            screen="status_pedido"
            detailTitle={formatMessage({ id: 'statusPage.creditCard.orderDetails' })}
            totalLabel={formatMessage({ id: 'statusPage.creditCard.total' })}
          />
        )}
        {paymentInfo}
        {!showSplitScreen && backToStoreUrl && (
          <Button
            size="medium"
            onClick={() => {
              analiticaStatusPageEvents.events.statusPage.backToStoreClicked();
              window.location.href = backToStoreUrl;
            }}
          >
            {formatMessage({ id: 'statusPage.backToStore' })}
          </Button>
        )}
        <BottomDetails />
      </div>
    </div>
  );

  if (!showSplitScreen) {
    return (
      <div className="min-h-screen h-full flex flex-col items-center justify-center bg-jade-background-body">
        <div className="flex flex-grow justify-between flex-wrap max-w-full md:max-w-[1440px] w-full flex-col md:flex-row">
          <div className="flex-grow h-full pb-jade-300 max-500:pb-0 mt-jade-300 md:mt-0">
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <SplitScreenComponent
      leftContentOnTop
      verticalAlignTop
      leftContent={leftContent || <></>}
      rightContent={<>{content}</>}
    />
  );
}
